/**
 *
 * useClientResources
 *
 */

import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';
import _orderBy from 'lodash/orderBy';
import { useQuery } from 'react-query';
import getRegionBySlug from 'containers/Main/services/getRegionBySlug.gql';
import { makeSelectProfileFilters } from 'containers/Auth/selectors';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectRegion,
} from 'containers/Main/selectors';
import useContentfulLocale from 'components/useContentfulLocale';
import GetHomeLinkResources from 'containers/HomePage/services/getHomeLinkResources';
import getLinkedResources from './services/getLinkedResources';

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientLanding: makeSelectClientLanding(),
  profileFilters: makeSelectProfileFilters(),
  region: makeSelectRegion(),
});

function useClientResources() {
  const ctfLocaleFilter = useContentfulLocale();
  const { clientDetails, clientLanding, profileFilters, region } = useSelector(
    stateSelector,
  );

  const { insuranceTags } = profileFilters;
  const { data: linkedResources } = useQuery(
    clientDetails && [
      'linked-client-resources',
      _get(clientDetails, 'sys.id'),
      ctfLocaleFilter,
    ],
    getLinkedResources,
  );
  const mappedLinkedResources = (linkedResources || []).map(item => ({
    ...item.fields,
    sys: item.sys,
  }));
  // fetch region
  const { data: regionData } = useQuery(
    region && ['region', region],
    getRegionBySlug,
  );

  const clientId = _get(clientDetails, 'sys.id');
  const { data: linkResources = [] } = useQuery(
    clientId && [
      'home-page-links',
      {
        clientId,
        ctfLocaleFilter,
        insuranceTags,
      },
    ],
    GetHomeLinkResources,
  );

  const hasMultipleLanding =
    _get(clientDetails, 'landingLinksCollection.items', []).length > 1;
  const landingClientResources = hasMultipleLanding
    ? _get(clientLanding, 'clientResourcesCollection.items', [])
    : [];
  const clientResources = _get(
    clientDetails,
    'clientResourcesCollection.items',
    [],
  );
  const regionClientResources = _get(
    regionData,
    'clientResourcesCollection.items',
    [],
  );
  const finalClientResources = _uniqBy(
    [
      ...clientResources,
      ...regionClientResources,
      ...landingClientResources,
      ...mappedLinkedResources,
    ],
    'sys.id',
  ).filter(el => !!el);
  const allClientResources = _orderBy(
    [...linkResources, ...finalClientResources],
    resource => _get(resource, 'homeOrder'),
    ['desc'],
  );

  return allClientResources;
}

export default useClientResources;
